import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { headerPropTypes, defaultProps } from 'shared/proptypes/Job';
import { addSuccessNotification, addErrorNotification } from 'shared/lib/notifications';
import { getPathname } from 'shared/lib/jobSearchQuery';
import CustomButton from 'shared/components/buttons/Custom';
import DeleteButton from 'shared/components/buttons/Delete';
import Tag from 'shared/components/Tag';
import Score from 'shared/components/Score';
import CompanyName from 'shared/components/CompanyName';
import googleAnalytics from 'userportal/lib/googleAnalytics';
import { selectors as favoriteJobsSelectors, actions as favoriteJobsActions } from 'userportal/ducks/favoriteJobs';
import { userportal as routes, createRoute, createStaticsLink } from 'shared/routes';
import styles from './Header.module.scss';

const JobHeader = ({
  job,
  withLinkToDetail,
  className,
  children,
  score,
  withFavorite,
  withRoles,
  // matcher, // TODO: docasne vypnuto pro vector scoring
  onAddRoleToProfile,
}) => {
  const dispatch = useDispatch();
  const loggedIn = useSelector(state => state.auth.loggedIn);
  const isJobFavorite = useSelector(state => favoriteJobsSelectors.isJobFavorite(state, job.id));
  const { t } = useTranslation('userportal');
  const navigate = useNavigate();
  const detailLink = createRoute(routes.job.detail, { slug: job.slug });

  const handleAddFavoriteJob = () =>
    dispatch(favoriteJobsActions.addFavoriteJob(
      job.id,
      () => addSuccessNotification(t('userportal:notification.favoriteJobAdded')),
      () => addErrorNotification(t('userportal:notification.favoriteJobAddFailed'))
    ));

  const handleDeleteFavoriteJob = () =>
    dispatch(favoriteJobsActions.deleteFavoriteJob(
      job.id,
      () => addSuccessNotification(t('userportal:notification.favoriteJobDeleted')),
      () => addErrorNotification(t('userportal:notification.favoriteJobDeleteFailed'))
    ));

  return (
    <div className={cn(styles.header, className)}>
      {job.company.logo && (
        <div className={styles.logo}>
          <div className={styles.imgWrapper}>
            <img src={createStaticsLink(job.company.logo)} title={job.company.name} alt={job.company.name} />
          </div>
        </div>
      )}
      <div className={styles.title}>
        <h1>
          {job.title}
        </h1>
        {!withLinkToDetail && (
          <CompanyName
            company={job.company}
            elementType="span"
            linkTo={createRoute(routes.company.detail, { slug: job.company.slug })}
            onClick={() => googleAnalytics.event('company_name_click', { source: 'job_header' })}
          />
        )}
        {(withRoles || (withFavorite && loggedIn)) && (
          <div className={styles.rolesFavoriteRow}>
            {withRoles && job.roles && job.roles.map(role => (
              <Tag
                key={role.slug}
                slug={role.slug}
                type="role"
                app="userportal"
                wrapperClassName={styles.roleTag}
                // match={matcher.getRoleMatch(role)} // TODO: docasne vypnuto pro vector scoring
                onAddToProfile={onAddRoleToProfile(role)}
                linkUrl={!loggedIn ? getPathname([{ type: 'role', slug: role.slug }]) : null}
              >
                {role.title}
              </Tag>
            ))}
            {withFavorite && loggedIn && (
              <div className={cn(styles.favorite, { [styles.favorited]: isJobFavorite })}>
                {isJobFavorite ? (
                  <>
                    <span>
                      <i className="lar la-heart" /> {t('userportal:job.favorite')}
                    </span>
                    <DeleteButton onClick={handleDeleteFavoriteJob}>{t('common:linkButton.delete')}</DeleteButton>
                  </>
                ) : (
                  <CustomButton iconClassName="lar la-heart" onClick={handleAddFavoriteJob}>{t('common:linkButton.addFavoriteJob')}</CustomButton>
                )}
              </div>
            )}
          </div>
        )}

        {!!children && <div className={styles.children}>{children}</div>}
        {withLinkToDetail && (
          <div className={styles.linkToDetail}>
            <CustomButton iconClassName="la la-arrow-circle-right" onClick={() => navigate(detailLink)}>{t('common:linkButton.jobDetail')}</CustomButton>
          </div>
        )}
        <div className="clearer" />
        {score !== null && (
          <p className={styles.textScore}>
            {t('common:score.your', { score, scoreName: t('common:scoreName') })}
          </p>
        )}
      </div>
      {score !== null && (
        <div className={styles.score}>
          <Score score={score} type="your" />
        </div>
      )}
    </div>
  );
};

JobHeader.propTypes = {
  job: PropTypes.shape(headerPropTypes),
  withLinkToDetail: PropTypes.bool,
  className: PropTypes.string,
  score: PropTypes.number,
  withRoles: PropTypes.bool,
  withFavorite: PropTypes.bool,
  matcher: PropTypes.shape({
    getRoleMatch: PropTypes.func,
  }),
  onAddRoleToProfile: PropTypes.func,
};

JobHeader.defaultProps = {
  job: defaultProps,
  className: null,
  score: null,
  withRoles: false,
  withFavorite: false,
  withLinkToDetail: false,
  matcher: {
    getRoleMatch() {}
  },
  onAddRoleToProfile() {},
};

export default JobHeader;
